// Fiches fournisseurs (achats et comptabilité).
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, IconButton,
  Snackbar, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { AddOutlined, DeleteOutline, EditOutlined, StarOutlineOutlined } from '@mui/icons-material';
import Layout from '../../components/Layout';
import { achatsAPI } from '../../services/api';
import { apiError, dateFr, today } from '../../utils/format';

const EMPTY = { nom: '', contact: '', telephone: '', email: '', adresse: '', ninea: '', rccm: '', delai_paiement: 30, notes: '', is_active: true };
const QUALIF = { QUALIFIE: ['Qualifié', 'success'], SOUS_SURVEILLANCE: ['Sous surveillance', 'warning'], NON_QUALIFIE: ['Non qualifié', 'error'], NON_EVALUE: ['Non évalué', 'default'] };

function EvaluationDialog({ fournisseur, onClose, onInfo }) {
  const [hist, setHist] = useState([]);
  const [error, setError] = useState('');
  const [f, setF] = useState(null);
  const charger = useCallback(() => { if (fournisseur) achatsAPI.evaluationsFournisseur(fournisseur.id).then((r) => setHist(r.data)).catch(() => {}); }, [fournisseur]);
  useEffect(() => { charger(); setF(null); setError(''); }, [charger]);
  if (!fournisseur) return null;
  const save = async () => {
    setError('');
    try { const r = await achatsAPI.evaluerFournisseur(fournisseur.id, f); setF(null); onInfo(r.data.message); charger(); } catch (e) { setError(apiError(e)); }
  };
  const suppr = async (id) => { if (!window.confirm('Supprimer cette évaluation ?')) return; try { await achatsAPI.supprimerEvaluation(id); charger(); } catch (e) { setError(apiError(e)); } };
  const note = (k, label) => <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="number" label={label} value={f[k] ?? ''} onChange={(e) => setF({ ...f, [k]: e.target.value })} inputProps={{ min: 0, max: 20, step: 0.5 }} /></Grid>;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Évaluation — {fournisseur.nom}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!f && <Button variant="contained" startIcon={<StarOutlineOutlined />} sx={{ mb: 2 }} onClick={() => setF({ date: today(), periode: '', note_qualite: '', note_delai: '', note_prix: '', note_service: '', commentaire: '' })}>Nouvelle évaluation</Button>}
        {f && (
          <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={4}><TextField fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Grid>
              <Grid item xs={6} sm={4}><TextField fullWidth size="small" label="Période" placeholder="ex. 2026" value={f.periode} onChange={(e) => setF({ ...f, periode: e.target.value })} /></Grid>
              <Grid item xs={12} sm={12} sx={{ display: { sm: 'none' } }} />
              {note('note_qualite', 'Qualité /20')}
              {note('note_delai', 'Délai /20')}
              {note('note_prix', 'Prix /20')}
              {note('note_service', 'Service /20')}
              <Grid item xs={12}><TextField fullWidth size="small" multiline label="Commentaire" value={f.commentaire} onChange={(e) => setF({ ...f, commentaire: e.target.value })} /></Grid>
              <Grid item xs={12} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Button onClick={() => setF(null)}>Annuler</Button>
                <Button variant="contained" onClick={save}>Enregistrer l'évaluation</Button>
              </Grid>
            </Grid>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 1 }}>Note moyenne des critères renseignés. Qualification : ≥ 14 qualifié, 10–14 sous surveillance, &lt; 10 non qualifié.</Typography>
          </Box>
        )}
        <Table size="small">
          <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Période</TableCell><TableCell align="center">Qualité</TableCell><TableCell align="center">Délai</TableCell><TableCell align="center">Prix</TableCell><TableCell align="center">Service</TableCell><TableCell align="center">Globale</TableCell><TableCell>Qualif.</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {hist.map((e) => (
              <TableRow key={e.id} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateFr(e.date)}</TableCell>
                <TableCell>{e.periode || '—'}{e.commentaire && <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>{e.commentaire}</Typography>}</TableCell>
                <TableCell align="center">{e.note_qualite ?? '—'}</TableCell><TableCell align="center">{e.note_delai ?? '—'}</TableCell>
                <TableCell align="center">{e.note_prix ?? '—'}</TableCell><TableCell align="center">{e.note_service ?? '—'}</TableCell>
                <TableCell align="center"><b>{e.note_globale}/20</b></TableCell>
                <TableCell><Chip size="small" color={QUALIF[e.qualification][1]} label={QUALIF[e.qualification][0]} /></TableCell>
                <TableCell padding="checkbox"><IconButton size="small" onClick={() => suppr(e.id)}><DeleteOutline fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
            {!hist.length && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 2, color: 'text.secondary' }}>Aucune évaluation.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Fermer</Button></DialogActions>
    </Dialog>
  );
}

const Fournisseurs = () => {
  const [rows, setRows] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [search, setSearch] = useState('');
  const [f, setF] = useState(null);
  const [dlgError, setDlgError] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [evaluer, setEvaluer] = useState(null);

  const load = useCallback(() => {
    achatsAPI.fournisseurs(showAll).then((r) => setRows(r.data)).catch((e) => setError(apiError(e)));
  }, [showAll]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setDlgError('');
    try {
      const data = { ...f, delai_paiement: Number(f.delai_paiement) };
      if (f.id) await achatsAPI.updateFournisseur(f.id, data); else await achatsAPI.createFournisseur(data);
      setToast(f.id ? 'Fournisseur modifié' : 'Fournisseur ajouté'); setF(null); load();
    } catch (e) { setDlgError(apiError(e)); }
  };
  const q = search.trim().toLowerCase();
  const list = rows.filter((r) => !q || `${r.nom} ${r.contact || ''} ${r.telephone || ''}`.toLowerCase().includes(q));
  const field = (k, label, props = {}) => (
    <TextField fullWidth size="small" label={label} value={f[k] ?? ''} onChange={(e) => setF({ ...f, [k]: e.target.value })} {...props} />
  );

  return (
    <Layout title="Fournisseurs" plain
      actions={<Button variant="contained" startIcon={<AddOutlined />} onClick={() => { setDlgError(''); setF({ ...EMPTY }); }}>Nouveau fournisseur</Button>}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField size="small" label="Rechercher" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 260 }} />
        <FormControlLabel control={<Switch checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />} label="Afficher les fournisseurs désactivés" />
      </Box>
      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fournisseur</TableCell><TableCell>Contact</TableCell><TableCell>Téléphone</TableCell><TableCell>NINEA</TableCell>
                <TableCell>Qualité fournisseur</TableCell><TableCell align="right">Délai paiement</TableCell><TableCell align="right">Commandes</TableCell><TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {list.map((r) => (
                <TableRow key={r.id} hover sx={{ opacity: r.is_active ? 1 : 0.55 }}>
                  <TableCell><b>{r.nom}</b>{!r.is_active && <Chip size="small" label="Désactivé" sx={{ ml: 1 }} />}
                    {r.adresse && <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{r.adresse}</Box>}</TableCell>
                  <TableCell>{r.contact}{r.email && <Box sx={{ fontSize: 12, color: 'text.secondary' }}>{r.email}</Box>}</TableCell>
                  <TableCell>{r.telephone}</TableCell>
                  <TableCell>{r.ninea}</TableCell>
                  <TableCell>
                    <Chip size="small" color={QUALIF[r.qualification || 'NON_EVALUE'][1]} variant={r.qualification && r.qualification !== 'NON_EVALUE' ? 'filled' : 'outlined'} label={QUALIF[r.qualification || 'NON_EVALUE'][0]} />
                    {r.derniere_note != null && <Box sx={{ fontSize: 12, color: 'text.secondary', mt: 0.3 }}>{r.derniere_note}/20{r.derniere_periode ? ` · ${r.derniere_periode}` : ''}</Box>}
                  </TableCell>
                  <TableCell align="right">{r.delai_paiement} j</TableCell>
                  <TableCell align="right">{r.nb_commandes}</TableCell>
                  <TableCell padding="checkbox" sx={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title="Évaluer"><IconButton size="small" aria-label={`Évaluer ${r.nom}`} onClick={() => setEvaluer(r)}><StarOutlineOutlined fontSize="small" /></IconButton></Tooltip>
                    <IconButton size="small" aria-label={`Modifier ${r.nom}`} onClick={() => { setDlgError(''); setF({ ...EMPTY, ...r }); }}><EditOutlined fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {list.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 3 }}>Aucun fournisseur</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
      <Dialog open={!!f} onClose={() => setF(null)} fullWidth maxWidth="sm">
        {f && (
          <>
            <DialogTitle>{f.id ? f.nom : 'Nouveau fournisseur'}</DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
              {dlgError && <Alert severity="error" sx={{ mb: 2 }}>{dlgError}</Alert>}
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12}>{field('nom', 'Raison sociale', { required: true })}</Grid>
                <Grid item xs={12} sm={6}>{field('contact', 'Interlocuteur')}</Grid>
                <Grid item xs={12} sm={6}>{field('telephone', 'Téléphone')}</Grid>
                <Grid item xs={12} sm={6}>{field('email', 'Email')}</Grid>
                <Grid item xs={12} sm={6}>{field('delai_paiement', 'Délai de paiement (jours)', { type: 'number' })}</Grid>
                <Grid item xs={12}>{field('adresse', 'Adresse')}</Grid>
                <Grid item xs={12} sm={6}>{field('ninea', 'NINEA')}</Grid>
                <Grid item xs={12} sm={6}>{field('rccm', 'RCCM')}</Grid>
                <Grid item xs={12}>{field('notes', 'Notes', { multiline: true, minRows: 2 })}</Grid>
                <Grid item xs={12}>
                  <FormControlLabel control={<Switch checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} />} label="Fournisseur actif" />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setF(null)}>Annuler</Button>
              <Button variant="contained" onClick={save}>Enregistrer</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <EvaluationDialog fournisseur={evaluer} onClose={() => { setEvaluer(null); load(); }} onInfo={setToast} />
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Layout>
  );
};

export default Fournisseurs;
